import React, { useState } from "react";
import { NavigateFunction } from "react-router-dom";
import DeleteIcon from "../../../../../images/products/trash-blue.svg";
import EditIcon from "../../../../../images/products/edit-blue.svg";
import PaymentMethodIcon from "../../../../../images/payment/bank-ach-icon.svg";
import RenderPaymentMethod from "./RenderPaymentMethod";
import RenderPaymentMethodType from "./RenderPaymentMethodType";
import UpsertPaymentMethodPopup from "./UpsertPaymentMethodPopup";
import DeletePaymentMethodPopUp from "./DeletePaymentMethodPopUp";
import {
  ContactReferenceModel,
  RelatedPaymentMethodGridModel,
  UpsertPaymentMethodModel,
} from "../models";
import {
  GridNames,
  NewPaymentRelatedSource,
  PaymentMethodPriority,
} from "../enums";
import {
  CustomButton,
  FlexContainer,
  ITableHeaders,
  TableWithHeaderButtons,
  withRouter,
} from "@mindmemobile/mm-shared-global";
import PlaceholderSmallerGrid from "./PlaceholderSmallerGrid";
import TableGrid from "./TableGrid";
import { RolesAndPermissionStore } from "../store";
import { AuthService } from "../services";

type IProps = {
  navigate: NavigateFunction;
  paymentMethods: RelatedPaymentMethodGridModel[];
  contact: ContactReferenceModel;
  source: keyof typeof NewPaymentRelatedSource;
  sourceId: string; //id of contact, subscription, invoice, checkout, purchaselinks
  onUpdate: () => void;
  rolesAndPermissionStore: RolesAndPermissionStore;
  disableNewButton?: boolean;
};
const RelatedPaymentMethodGrid: React.FC<IProps> = ({
  paymentMethods,
  contact,
  source,
  sourceId,
  onUpdate,
  rolesAndPermissionStore,
  disableNewButton
}) => {
  const [deletePaymentMethod, setDeletePaymentDetails] =
    useState<RelatedPaymentMethodGridModel | null>(null);
  const [showPaymentMethodPopup, setShowPaymentMethodPopUp] = useState(false);
  const [editPaymentMethod, setEditPaymentMethod] =
    useState<UpsertPaymentMethodModel | null>(null);

  function onEditClick(data: RelatedPaymentMethodGridModel) {
    if (!rolesAndPermissionStore.hasManagePermission("Edit")) return;
    setEditPaymentMethod(
      new UpsertPaymentMethodModel({
        contact: contact,
        source: source,
        sourceId: sourceId,
        paymentMethodId: data.paymentMethod?.id,
        contactId: contact.id,
        id: data.id,
      })
    );
    setShowPaymentMethodPopUp(true);
  }

  const headers = () => {
    const columns: ITableHeaders<RelatedPaymentMethodGridModel>[] = [
      {
        dataKey: "status",
        title: "Status",
        onCellRender: (data: RelatedPaymentMethodGridModel) => {
          return (
            <CustomButton className="status-btn">{data.status}</CustomButton>
          );
        },
      },
      {
        dataKey: "paymentMethod",
        title: "Payment Method",
        onCellRender: (data: RelatedPaymentMethodGridModel) => {
          return <RenderPaymentMethod paymentMethod={data.paymentMethod} />;
        },
      },
      {
        dataKey: "expirationDate",
        title: "Expiration",
        onCellRender: (data: RelatedPaymentMethodGridModel) => {
          return data.expirationDate;
        },
      },
      {
        dataKey: "activity",
        title: "Activity",
        onCellRender: (data: RelatedPaymentMethodGridModel) => {
          return data.activity;
        },
      },
      {
        dataKey: "paymentMethod",
        title: "Type",
        onCellRender: (data: RelatedPaymentMethodGridModel) => {
          return data.paymentMethod ? (
            <RenderPaymentMethodType type={data.paymentMethod.type} />
          ) : (
            "-"
          );
        },
      },
      {
        dataKey: "isPrimary",
        title: "Primary",
        onCellRender: (data: RelatedPaymentMethodGridModel) => {
          return (
            <CustomButton
              bgColor={!data.isPrimary ? "#ECF0FF" : ""}
              color={!data.isPrimary ? "#6A7AB5" : ""}
            >
              {data.priority == PaymentMethodPriority.Primary
                ? "Primary"
                : data.priority == PaymentMethodPriority.Backup
                  ? "Backup"
                  : "None"}
            </CustomButton>
          );
        },
      },
      {
        dataKey: "isPrimary",
        title: "Actions",
        className: "table-col-10 text-center",
        cellClassName: "table-col-10 text-center",
        onCellClick: (e) => {
          e.stopPropagation();
        },
        onCellRender: (data: RelatedPaymentMethodGridModel) => {
          return (
            <FlexContainer
              alignItems="center"
              justifyContent="center"
              spacing={10}
            >
              <img
                src={EditIcon}
                alt="Edit"
                onClick={() => onEditClick(data)}
              />
              {paymentMethods.length > 1 && !data.isPrimary && (
                <img
                  src={DeleteIcon}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!rolesAndPermissionStore.hasManagePermission("Edit"))
                      return;
                    setDeletePaymentDetails(data);
                  }}
                  className="img-15 clickable"
                />
              )}
            </FlexContainer>
          );
        },
      },
    ];
    return columns;
  };

  const togglePopUp = (show: boolean, refresh: boolean) => {
    setShowPaymentMethodPopUp(show);
    if (!show) setEditPaymentMethod(null);
    if (refresh) {
      onUpdate();
    }
  };

  const paymentMethodPopupContent = showPaymentMethodPopup && (
    <UpsertPaymentMethodPopup
      paymentMethodData={
        editPaymentMethod ||
        new UpsertPaymentMethodModel({
          contact: contact,
          contactId: contact.id,
          source: source,
          sourceId: sourceId,
        })
      }
      onClose={(refresh: boolean) => togglePopUp(false, refresh)}
    />
  );

  if (paymentMethods.length == 0)
    return (
      <>
        {paymentMethodPopupContent}
        <PlaceholderSmallerGrid
          title="No Payment Methods"
          icon={PaymentMethodIcon}
          buttonCaption="New"
          disableButton={disableNewButton === true}
          buttonClick={() => {
            if (!rolesAndPermissionStore.hasManagePermission("Edit")) return;
            togglePopUp(true, false);
          }}
        />
      </>
    );

  return (
    <>
      {paymentMethodPopupContent}
      <TableGrid
        wrapperClassName="table-with-header-btns table-header-max-height"
        tableHeader={
          <TableWithHeaderButtons
            isShowHeaderButton={true}
            totalItems={paymentMethods.length}
            icon={PaymentMethodIcon}
            title="Payment Method"
            onNewButtonClick={() => {
              if (!rolesAndPermissionStore.hasManagePermission("Edit")) return;
              togglePopUp(true, false);
            }}
          />
        }
        cellSpacing={20}
        headers={headers()}
        data={paymentMethods}
        totalItemCount={paymentMethods.length}
        name={GridNames.Payment}
      />
      {deletePaymentMethod && (
        <DeletePaymentMethodPopUp
          paymentMethod={deletePaymentMethod}
          onClose={(refresh) => {
            setDeletePaymentDetails(null);
            refresh && onUpdate();
          }}
        />
      )}
    </>
  );
};

export default withRouter(RelatedPaymentMethodGrid);
